import React, { useState, useEffect, useMemo } from "react";
import { itemsAPI } from "../utils/api";
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import "./PainelAnalise.css";

const STATUSES = [
  "TODOS OS STATUS",
  "DISPONÍVEL EM ESTOQUE",
  "COM TÉCNICO",
  "UPC",
  "DEVOLVIDA COMO USADA",
  "DEVOLVIDA COMO DEFEITO",
  "DEVOLVIDA COMO NOVA",
  "ERRO DE BILLING",
  "ENTREGUE TRANSPORTADORA",
  "REQUISIÇÃO CANCELADA",
  "SERVICE DE RETORNO",
  "ENVIADO PARA UDI",
  "ENVIADO PARA CSP SSA",
  "ENVIADO PARA INSS SSA",
  "ENVIADO PARA INSS BARRA",
  "ENVIADO PARA CRC",
  "PRODUTO NOVO",
  "REPARO DE ESTOQUE",
  "BAIXAR PART SALES",
];

const AGE_RANGES = [
  { label: "01 A 30 DIAS", min: 1, max: 30 },
  { label: "31 A 50 DIAS", min: 31, max: 50 },
  { label: "51 A 60 DIAS", min: 51, max: 60 },
  { label: "61 A 90 DIAS", min: 61, max: 90 },
  { label: "91 A 110 DIAS", min: 91, max: 110 },
  { label: "111 A 120 DIAS", min: 111, max: 120 },
  { label: "121 A 180 DIAS", min: 121, max: 180 },
  { label: "181 A 200 DIAS", min: 181, max: 200 },
  { label: "200+ DIAS", min: 201, max: Infinity },
];

const brl = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Função para calcular idade da NF em dias
const calculateNFAge = (dataNF) => {
  if (!dataNF) return 0;
  const nfDate = new Date(dataNF);
  const today = new Date();
  const diffTime = Math.abs(today - nfDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Função para gerar planilha Excel com formatação profissional
const generateExcelForRange = async (rangeLabel, items, stats) => {
  if (!items || items.length === 0) {
    alert('Nenhum item encontrado para esta faixa etária.');
    return;
  }

  try {
    // Criar novo workbook
    const workbook = new ExcelJS.Workbook();
    
    // Configurar propriedades do documento
    workbook.creator = 'Sistema de Gestão de Estoque';
    workbook.lastModifiedBy = 'Sistema de Gestão de Estoque';
    workbook.created = new Date();
    workbook.modified = new Date();
    
    // Criar worksheet
    const worksheet = workbook.addWorksheet('Relatório de Análise', {
      pageSetup: {
        paperSize: 9, // A4
        orientation: 'landscape',
        margins: {
          left: 0.7,
          right: 0.7,
          top: 0.75,
          bottom: 0.75,
          header: 0.3,
          footer: 0.3
        }
      }
    });

    // Definir estilos
    const titleStyle = {
      font: { name: 'Arial', size: 18, bold: true, color: { argb: 'FF2F4F4F' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F3FF' } }
    };

    const subtitleStyle = {
      font: { name: 'Arial', size: 14, bold: true, color: { argb: 'FF2F4F4F' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F8FF' } }
    };

    const headerStyle = {
      font: { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
      border: {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      }
    };

    const dataStyle = {
      font: { name: 'Arial', size: 10 },
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: {
        top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
      }
    };

    const summaryStyle = {
      font: { name: 'Arial', size: 12, bold: true },
      alignment: { horizontal: 'left', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } }
    };

    const currencyStyle = {
      font: { name: 'Arial', size: 10 },
      alignment: { horizontal: 'right', vertical: 'middle' },
      numFmt: '"R$" #,##0.00',
      border: {
        top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
      }
    };

    // Título principal
    worksheet.mergeCells('A1:P1');
    worksheet.getCell('A1').value = 'RELATÓRIO DE ANÁLISE POR IDADE DA NOTA FISCAL';
    worksheet.getCell('A1').style = titleStyle;
    worksheet.getRow(1).height = 30;

    // Subtítulo com faixa etária
    worksheet.mergeCells('A2:P2');
    worksheet.getCell('A2').value = `Faixa Etária: ${rangeLabel}`;
    worksheet.getCell('A2').style = subtitleStyle;
    worksheet.getRow(2).height = 25;

    // Data de geração
    worksheet.mergeCells('A3:P3');
    worksheet.getCell('A3').value = `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
    worksheet.getCell('A3').style = { ...subtitleStyle, font: { ...subtitleStyle.font, size: 10 } };
    worksheet.getRow(3).height = 20;

    // Linha vazia
    worksheet.getRow(4).height = 10;

    // Seção de resumo executivo
    worksheet.mergeCells('A5:P5');
    worksheet.getCell('A5').value = 'RESUMO EXECUTIVO';
    worksheet.getCell('A5').style = summaryStyle;
    worksheet.getRow(5).height = 25;

    const avgValue = stats.count > 0 ? stats.value / stats.count : 0;
    worksheet.getCell('A6').value = `Total de Itens: ${stats.count}`;
    worksheet.getCell('A6').style = summaryStyle;
    worksheet.getCell('A7').value = `Valor Total: R$ ${stats.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    worksheet.getCell('A7').style = summaryStyle;
    worksheet.getCell('A8').value = `Valor Médio por Item: R$ ${avgValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    worksheet.getCell('A8').style = summaryStyle;

    // Linha vazia
    worksheet.getRow(9).height = 10;

    // Cabeçalhos da tabela
    const headers = [
      'CÓDIGO SISTEMA',
      'NÚMERO DA PEÇA',
      'DESCRIÇÃO DA PEÇA',
      'NOTA FISCAL',
      'DATA DA NOTA FISCAL',
      'IDADE EM DIAS',
      'VALOR UNITÁRIO (R$)',
      'VALOR TOTAL (R$)',
      'STATUS ATUAL',
      'LOCALIZAÇÃO FÍSICA',
      'LOTE',
      'ORDEM DE SERVIÇO',
      'CÓDIGO DE ENTREGA',
      'NÚMERO DO PEDIDO',
      'DATA DA ÚLTIMA ALTERAÇÃO',
      'OBSERVAÇÕES'
    ];

    // Adicionar cabeçalhos
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(10, index + 1);
      cell.value = header;
      cell.style = headerStyle;
    });
    worksheet.getRow(10).height = 25;

    // Adicionar dados
    items.forEach((item, rowIndex) => {
      const row = 11 + rowIndex;
      const rowData = [
        item.cod_sis || '',
        item.part_number || '',
        item.descricao || '',
        item.nf || '',
        item.data_nf || '',
        calculateNFAge(item.data_nf),
        Number(item.valor_unit) || 0,
        Number(item.valor_total) || 0,
        item.status || '',
        item.localizacao_fisica || '',
        item.lote || '',
        item.os_relacionada || '',
        item.delivery || '',
        item.pedido_cliente || '',
        item.ultima_alteracao || '',
        '' // Observações vazias para futuras anotações
      ];

      rowData.forEach((value, colIndex) => {
        const cell = worksheet.getCell(row, colIndex + 1);
        cell.value = value;
        
        // Aplicar estilo base
        cell.style = dataStyle;
        
        // Formatação específica por coluna
        if (colIndex === 5) { // IDADE EM DIAS
          cell.style = { ...dataStyle, alignment: { horizontal: 'center', vertical: 'middle' } };
        } else if (colIndex === 6 || colIndex === 7) { // VALORES MONETÁRIOS
          cell.style = currencyStyle;
        } else if (colIndex === 4) { // DATA DA NOTA FISCAL
          cell.style = { ...dataStyle, alignment: { horizontal: 'center', vertical: 'middle' } };
        }
      });
      
      worksheet.getRow(row).height = 20;
    });

    // Definir larguras das colunas
    const columnWidths = [15, 20, 40, 12, 15, 12, 15, 15, 20, 20, 15, 15, 15, 15, 20, 25];
    columnWidths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });

    // Adicionar filtros automáticos
    worksheet.autoFilter = 'A10:P' + (10 + items.length);

    // Adicionar congelamento de painéis
    worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 10 }];

    // Adicionar rodapé
    const lastRow = 11 + items.length;
    worksheet.mergeCells(`A${lastRow + 2}:P${lastRow + 2}`);
    worksheet.getCell(`A${lastRow + 2}`).value = 'Relatório gerado automaticamente pelo Sistema de Gestão de Estoque';
    worksheet.getCell(`A${lastRow + 2}`).style = {
      font: { name: 'Arial', size: 9, italic: true },
      alignment: { horizontal: 'center', vertical: 'middle' }
    };

    // Gerar arquivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    // Download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_analise_${rangeLabel.replace(/\s+/g, '_').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Erro ao gerar Excel:', error);
    alert('Erro ao gerar o arquivo Excel. Tente novamente.');
  }
};

export default function PainelAnalise({ onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("TODOS OS STATUS");

  // Carregar dados
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await itemsAPI.getItems();
      const loadedItems = (response.data || []).map(it => ({
        ...it,
        log: Array.isArray(it.logs) ? it.logs : (it.log || [])
      }));
      console.log(`📊 Total de itens carregados: ${loadedItems.length}`);
      console.log(`📊 Itens por status:`, loadedItems.reduce((acc, item) => {
        acc[item.status] = (acc[item.status] || 0) + 1;
        return acc;
      }, {}));
      setItems(loadedItems);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar itens por status selecionado
  const filteredItems = useMemo(() => {
    if (selectedStatus === "TODOS OS STATUS") {
      return items;
    }
    return items.filter(item => item.status === selectedStatus);
  }, [items, selectedStatus]);


  // Calcular estatísticas por faixa etária
  const ageRangeStats = useMemo(() => {
    const stats = {};
    
    AGE_RANGES.forEach(range => {
      const itemsInRange = filteredItems.filter(item => {
        const age = calculateNFAge(item.data_nf);
        return age >= range.min && (range.max === Infinity || age <= range.max);
      });
      
      const totalValue = itemsInRange.reduce((sum, item) => sum + (Number(item.valor_total) || 0), 0);
      const totalItems = itemsInRange.length;
      
      stats[range.label] = {
        value: totalValue,
        count: totalItems,
        items: itemsInRange
      };
    });
    
    return stats;
  }, [filteredItems]);

  // Calcular totais gerais
  const totalStats = useMemo(() => {
    const totalValue = filteredItems.reduce((sum, item) => sum + (Number(item.valor_total) || 0), 0);
    const totalItems = filteredItems.length;
    
    return {
      value: totalValue,
      count: totalItems
    };
  }, [filteredItems]);

  if (loading) {
    return (
      <div className="painel-loading">
        <div className="loading-spinner"></div>
        <p>Carregando dados de análise...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="painel-error">
        <h3>Erro ao carregar dados</h3>
        <p>{error}</p>
        <button onClick={loadData} className="btn btn-primary">Tentar novamente</button>
      </div>
    );
  }

  return (
    <div className="painel-analise">
      {/* Header */}
      <div className="painel-header">
        <div className="header-content">
          <h1 className="painel-title">Painel de Análise (Valor por Idade da NF)</h1>
          <button className="btn btn-outline back-btn" onClick={onBack}>
            ← Voltar para Gerenciamento
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="painel-filters">
        <div className="filter-section">
          <label className="filter-label">Selecione o STATUS para analisar:</label>
          <select 
            className="status-select" 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            {STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Resumo Geral */}
      <div className="painel-summary">
        <div className="summary-card total-card">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <h3>Total Geral</h3>
            <div className="summary-value">{brl(totalStats.value)}</div>
            <div className="summary-count">Total de itens: {totalStats.count}</div>
          </div>
        </div>
      </div>

      {/* Cards de Faixas Etárias */}
      <div className="age-ranges-grid">
        {AGE_RANGES.map((range, index) => {
          const stats = ageRangeStats[range.label];
          return (
            <div key={range.label} className="age-card">
              <div className="age-card-header">
                <h3 className="age-range">{range.label}</h3>
                <div className="age-icon">📅</div>
              </div>
              <div className="age-card-content">
                <div className="age-value">{brl(stats.value)}</div>
                <div className="age-count">
                  <span className="count-icon">ℹ️</span>
                  Total de itens: {stats.count}
                </div>
              </div>
              {stats.count > 0 && (
                <div className="age-details">
                  <div className="detail-item">
                    <span className="detail-label">Valor médio:</span>
                    <span className="detail-value">{brl(stats.value / stats.count)}</span>
                  </div>
                  <div className="age-actions">
                    <button 
                      className="download-btn"
                      onClick={() => generateExcelForRange(range.label, stats.items, stats)}
                      title="Baixar planilha com peças deste prazo"
                    >
                      📊 Baixar Planilha
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Gráfico de Distribuição (opcional) */}
      {totalStats.count > 0 && (
        <div className="distribution-chart">
          <h3>Distribuição por Faixa Etária</h3>
          <div className="chart-bars">
            {AGE_RANGES.map(range => {
              const stats = ageRangeStats[range.label];
              const percentage = totalStats.value > 0 ? (stats.value / totalStats.value) * 100 : 0;
              
              return (
                <div key={range.label} className="chart-bar">
                  <div className="bar-label">{range.label}</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill" 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <div className="bar-value">{brl(stats.value)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
